import { useState } from 'react'
import WalletPassQR from './WalletPassQR'

/**
 * WalletSignInPanel
 *
 * Collapsible panel in LoginPage that covers two flows:
 *
 *   A) User is signing in → show QR for wallet-based login
 *   B) User is authenticated → show "Add to Apple Wallet" / "Save to Google Wallet" buttons
 *
 * Props:
 *   mode:         'login' (show QR) | 'manage' (show add-to-wallet buttons)
 *   onToken:      called with session JWT when QR auth succeeds (login mode)
 *   isAuthenticated: whether a session currently exists
 */

interface Props {
  mode:            'login' | 'manage'
  onToken?:        (token: string) => void
  isAuthenticated?: boolean
}

// Detect platform for appropriate wallet button
function detectWalletPlatform(): 'apple' | 'google' | 'both' | 'none' {
  if (typeof navigator === 'undefined') return 'none'
  const ua = navigator.userAgent
  if (/iPhone|iPad|iPod|Macintosh/.test(ua)) return 'apple'
  if (/Android/.test(ua)) return 'google'
  return 'both'
}

// Apple Wallet SVG badge (official spec: white on black)
function AppleWalletBadge() {
  return (
    <svg viewBox="0 0 119 40" width="119" height="40" xmlns="http://www.w3.org/2000/svg"
         aria-label="Add to Apple Wallet">
      <rect width="119" height="40" rx="5" fill="#000"/>
      <text x="59.5" y="26" fontFamily="-apple-system, Helvetica Neue, sans-serif"
            fontSize="14" fontWeight="400" fill="#fff" textAnchor="middle">
        Add to  Wallet
      </text>
    </svg>
  )
}

// Google Wallet button (official brand colors)
function GoogleWalletButton({ onClick, loading }: { onClick: () => void; loading: boolean }) {
  return (
    <button
      onClick={onClick}
      disabled={loading}
      style={{
        display:         'flex',
        alignItems:      'center',
        gap:             '8px',
        padding:         '10px 16px',
        background:      '#fff',
        border:          '1px solid #dadce0',
        borderRadius:    '4px',
        cursor:          loading ? 'wait' : 'pointer',
        fontFamily:      '"Google Sans", Roboto, Arial, sans-serif',
        fontSize:        '14px',
        fontWeight:      500,
        color:           '#3c4043',
        whiteSpace:      'nowrap',
        opacity:         loading ? 0.7 : 1,
      }}
      aria-label="Save to Google Wallet"
    >
      {/* Google Wallet G icon */}
      <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
        <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
        <path d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
        <path d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
        <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
      </svg>
      {loading ? 'Saving…' : 'Save to Google Wallet'}
    </button>
  )
}

export default function WalletSignInPanel({ mode, onToken, isAuthenticated }: Props) {
  const [expanded,    setExpanded]    = useState(false)
  const [googleLoading, setGoogleLoading] = useState(false)
  const platform = detectWalletPlatform()

  // --- Login mode: QR-based wallet auth ---
  if (mode === 'login') {
    return (
      <div>
        <button
          onClick={() => setExpanded(!expanded)}
          style={{
            width:       '100%',
            display:     'flex',
            alignItems:  'center',
            justifyContent: 'center',
            gap:         '8px',
            padding:     '10px 0',
            background:  'none',
            border:      '1px solid rgba(147,51,234,0.2)',
            borderRadius: '6px',
            cursor:      'pointer',
            transition:  'border-color 0.2s',
          }}
          onMouseEnter={e => (e.currentTarget.style.borderColor = 'rgba(147,51,234,0.5)')}
          onMouseLeave={e => (e.currentTarget.style.borderColor = 'rgba(147,51,234,0.2)')}
          aria-expanded={expanded}
        >
          {/* Wallet icon */}
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="rgba(147,51,234,0.7)" strokeWidth="1.5" strokeLinecap="round">
            <rect x="2" y="5" width="20" height="14" rx="2"/>
            <path d="M16 12h2"/>
          </svg>
          <span style={{
            fontFamily:    'ui-monospace, SFMono-Regular, Menlo, monospace',
            fontSize:      '9px',
            fontWeight:    400,
            letterSpacing: '0.25em',
            textTransform: 'uppercase',
            color:         'rgba(147,51,234,0.7)',
          }}>
            Use LUKHΛS Pass
          </span>
        </button>

        {expanded && (
          <WalletPassQR
            onSuccess={(token) => { setExpanded(false); onToken?.(token) }}
            onError={() => setExpanded(false)}
          />
        )}
      </div>
    )
  }

  // --- Manage mode: add-to-wallet buttons ---
  if (!isAuthenticated) return null

  const handleAppleWallet = () => {
    window.location.href = '/api/wallet/apple/download'
  }

  const handleGoogleWallet = async () => {
    setGoogleLoading(true)
    try {
      const res  = await fetch('/api/wallet/google/save')
      const data = await res.json()
      if (data.ok && data.saveUrl) {
        window.open(data.saveUrl, '_blank')
      }
    } finally {
      setGoogleLoading(false)
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'flex-start' }}>
      <p style={{
        fontFamily:    'ui-monospace, SFMono-Regular, Menlo, monospace',
        fontSize:      '9px',
        fontWeight:    400,
        letterSpacing: '0.35em',
        textTransform: 'uppercase',
        color:         'rgba(255,255,255,0.35)',
        margin:        0,
      }}>
        Save your ΛiD pass
      </p>

      {(platform === 'apple' || platform === 'both') && (
        <button
          onClick={handleAppleWallet}
          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
          aria-label="Add to Apple Wallet"
        >
          <AppleWalletBadge />
        </button>
      )}

      {(platform === 'google' || platform === 'both') && (
        <GoogleWalletButton onClick={handleGoogleWallet} loading={googleLoading} />
      )}
    </div>
  )
}
